/**
 * The date-fns locale of an app language, so month and day names and AM/PM
 * are shown in that language ("12 Oct 2026", "12 окт. 2026", "12 அக். 2026").
 *
 * date-fns has no locale for Malayalam, Punjabi, Marathi or Urdu, so those
 * take the English layout with the names from the app's own translations
 * (the "Jan", "January", "Mon", "Monday", "AM" ... keys).
 */
import type { Locale } from "date-fns";
import {
  enGB,
  ta,
  te,
  kn,
  hi,
  bn,
  gu,
  zhCN,
  es,
  fr,
  ja,
  ru,
  de,
  it,
  ko,
  ar,
} from "date-fns/locale";
import i18n from "i18next";

const MONTHS = [
  ["Jan", "January"],
  ["Feb", "February"],
  ["Mar", "March"],
  ["Apr", "April"],
  ["May", "May"],
  ["Jun", "June"],
  ["Jul", "July"],
  ["Aug", "August"],
  ["Sep", "September"],
  ["Oct", "October"],
  ["Nov", "November"],
  ["Dec", "December"],
];
const DAYS = [
  ["Sun", "Sunday"],
  ["Mon", "Monday"],
  ["Tue", "Tuesday"],
  ["Wed", "Wednesday"],
  ["Thu", "Thursday"],
  ["Fri", "Friday"],
  ["Sat", "Saturday"],
];

/** English layout, names from the app's translations of the language. */
const translatedLocale = (lang: string): Locale => {
  const t = (key: string) => String(i18n.t(key, { lng: lang }));
  const name = ([short, wide]: string[], width?: string) =>
    width === "wide"
      ? t(wide)
      : width === "narrow"
        ? Array.from(t(short))[0] || ""
        : t(short);
  return {
    ...enGB,
    code: lang,
    localize: {
      ...enGB.localize,
      month: (month: number, options?: any) =>
        name(MONTHS[month], options?.width),
      day: (day: number, options?: any) => name(DAYS[day], options?.width),
      dayPeriod: (dayPeriod: string, options?: any) =>
        dayPeriod === "am"
          ? t("AM")
          : dayPeriod === "pm"
            ? t("PM")
            : enGB.localize.dayPeriod(dayPeriod as any, options),
    },
  };
};

const translated: Record<string, Locale> = {};
const translatedFor = (lang: string) =>
  (translated[lang] = translated[lang] || translatedLocale(lang));

export const localeMap: Record<string, Locale> = {
  en: enGB,
  ta,
  te,
  get ml() {
    return translatedFor("ml");
  },
  kn,
  hi,
  bn,
  get pa() {
    return translatedFor("pa");
  },
  get mr() {
    return translatedFor("mr");
  },
  gu,
  get ur() {
    return translatedFor("ur");
  },
  zh: zhCN,
  es,
  fr,
  ja,
  ru,
  de,
  it,
  ko,
  ar,
};

export const getDateFnsLocale = (lang?: string): Locale => {
  if (!lang) return enGB;

  const key = lang.split("-")[0]; // supports en-US, ta-IN etc.
  return localeMap[key] || enGB;
};
